import { Controller } from "@hotwired/stimulus"

const TEXT_SIZE_KEY = "ms-studio:text-size"
const BASE = { "--ui": 15, "--ui-sm": 13, "--ui-xs": 11.5 }

export default class extends Controller {
  static targets = [
    "versionPopover", "changelogPopover", "statusPopover",
    "statusPill", "statusLabel", "statusDot", "textSizeLabel",
  ]
  static values = {
    status: String,
    statusUrl: String,
    openCount: Number,
    textSize: { type: Number, default: 100 },
    versionLabel: String,
  }

  connect() {
    this.openPopover = null
    this.onOutside = this.onOutside.bind(this)
    this.onEsc = this.onEsc.bind(this)
    this.restoreTextSize()
  }

  disconnect() {
    document.removeEventListener("mousedown", this.onOutside)
    document.removeEventListener("keydown", this.onEsc)
  }

  // --- Popovers ------------------------------------------------------------

  toggleVersion() { this.togglePopover(this.versionPopoverTarget) }
  toggleChangelog() { this.togglePopover(this.hasChangelogPopoverTarget ? this.changelogPopoverTarget : null) }
  toggleStatus() { this.togglePopover(this.statusPopoverTarget) }

  togglePopover(popover) {
    if (!popover) return
    if (this.openPopover === popover) { this.closePopover(); return }
    this.closePopover()
    popover.hidden = false
    this.openPopover = popover
    document.addEventListener("mousedown", this.onOutside)
    document.addEventListener("keydown", this.onEsc)
  }

  closePopover() {
    if (this.openPopover) this.openPopover.hidden = true
    this.openPopover = null
    document.removeEventListener("mousedown", this.onOutside)
    document.removeEventListener("keydown", this.onEsc)
  }

  onOutside(event) {
    if (this.openPopover && !this.openPopover.parentElement.contains(event.target)) this.closePopover()
  }
  onEsc(event) { if (event.key === "Escape") this.closePopover() }

  // --- Status + approval gate ----------------------------------------------

  async chooseStatus(event) {
    const status = event.currentTarget.dataset.status
    this.closePopover()

    if (status === "approved" && this.openCountValue > 0) {
      const ok = window.confirm(`${this.openCountValue} notes are still open on ${this.versionLabelValue}. Approve anyway?`)
      if (!ok) return
    }

    const previous = this.statusValue
    const previousLabel = this.hasStatusLabelTarget ? this.statusLabelTarget.textContent : ""
    this.applyStatus(status, event.currentTarget.dataset.statusLabel)

    try {
      const response = await fetch(this.statusUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ status }),
      })
      const ct = response.headers.get("content-type") || ""
      if (!response.ok || !ct.includes("application/json")) throw new Error("bad response")
      const data = await response.json()
      this.applyStatus(data.status, data.label)
    } catch (error) {
      this.applyStatus(previous, previousLabel)
    }
  }

  applyStatus(status, label) {
    this.statusValue = status
    if (this.hasStatusLabelTarget && label) this.statusLabelTarget.textContent = label
    if (this.hasStatusPillTarget) {
      this.statusPillTarget.className = `ms-pill ms-studio__status-pill is-${status}`
    }
  }

  // --- Open-count from the rail --------------------------------------------

  commentsChanged(event) {
    if (typeof event.detail?.openCount === "number") this.openCountValue = event.detail.openCount
  }

  // --- Text size stepper ---------------------------------------------------

  smaller() { this.setTextSize(Math.max(85, this.textSizeValue - 5)) }
  larger() { this.setTextSize(Math.min(135, this.textSizeValue + 5)) }

  setTextSize(value, persist = true) {
    this.textSizeValue = value
    const root = this.studioRoot()
    if (root) {
      const scale = value / 100
      Object.entries(BASE).forEach(([k, v]) => root.style.setProperty(k, `${+(v * scale).toFixed(2)}px`))
    }
    if (this.hasTextSizeLabelTarget) this.textSizeLabelTarget.textContent = `${value}%`
    if (persist) {
      try { localStorage.setItem(TEXT_SIZE_KEY, String(value)) } catch (e) { /* ignore */ }
    }
  }

  restoreTextSize() {
    let value = this.textSizeValue
    try {
      const saved = Number(localStorage.getItem(TEXT_SIZE_KEY))
      if (saved >= 85 && saved <= 135) value = saved
    } catch (e) { /* ignore */ }
    this.setTextSize(value)
  }

  // "Reset workspace" (from the review controller): drop the persisted text
  // size and return to 100%.
  resetTextSize() {
    // Apply 100% WITHOUT persisting, then clear the key so it stays absent.
    this.setTextSize(100, false)
    try { localStorage.removeItem(TEXT_SIZE_KEY) } catch (e) { /* ignore */ }
  }

  studioRoot() {
    return this.element.closest(".ms-studio") || document.querySelector(".ms-studio")
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || ""
  }
}
